import { Controller } from "@hotwired/stimulus"
import { ALLOWED_IMAGE_TYPES, isAllowedImageType } from '../constants/file_types'

export default class extends Controller {
  static targets = ["basePhoto", "targetMedia", "basePlaceholder", "targetPlaceholder", "baseFileName", "targetFileName", "inputImage", "inputImagePlaceholder", "inputImageFileName"]

  loadBasePhoto(event) {
    const files = event.target.files;
    if (files.length > 0) {
      const file = files[0];
      if (!isAllowedImageType(file.type)) {
        this.showError('Base photo must be an image file (JPEG, PNG, or WebP). Please select a valid image file.');
        event.target.value = '';
        this.resetDropzone(this.basePhotoTarget, this.basePlaceholderTarget, this.baseFileNameTarget);
        return;
      }
    }
    this.loadMedia(event, this.basePhotoTarget, this.basePlaceholderTarget, this.baseFileNameTarget);
  }

  loadTargetMedia(event) {
    this.loadMedia(event, this.targetMediaTarget, this.targetPlaceholderTarget, this.targetFileNameTarget, true);
  }

  loadInputImage(event) {
    this.loadMedia(event, this.inputImageTarget, this.inputImagePlaceholderTarget, this.inputImageFileNameTarget);
  }

  loadMedia(event, element, placeholder, fileNameElement, isTarget = false) {
    const files = event.target.files;
    if (files.length === 0) {
      this.resetDropzone(element, placeholder, fileNameElement);
      if (isTarget) {
        this.dispatchFileType(null);
      }
      return;
    }

    const file = files[0];
    fileNameElement.textContent = file.name;
    fileNameElement.style.display = "block";

    if (isTarget) {
      this.dispatchFileType(file.type);
    }

    if (file.type.startsWith("video/")) {
      const video = document.createElement("video");
      video.preload = "metadata";
      
      // Create object URL for the video file
      const objectUrl = URL.createObjectURL(file);
      
      video.addEventListener("loadeddata", () => {
        // Seek to the first frame
        video.currentTime = 0;
      });

      video.addEventListener("seeked", () => {
        // Now that we've seeked to the first frame, capture it
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        element.style.backgroundImage = `url('${canvas.toDataURL("image/jpeg", 0.8)}')`;
        element.style.backgroundSize = "cover";
        element.style.backgroundPosition = "center";
        element.classList.add("hover:brightness-50");
        placeholder.style.display = "none";
        
        // Clean up
        URL.revokeObjectURL(objectUrl);
      });

      // Set the video source last
      video.src = objectUrl;
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        element.style.backgroundImage = `url('${event.target.result}')`;
        element.style.backgroundSize = "cover";
        element.style.backgroundPosition = "center";
        element.classList.add("hover:brightness-50");
        placeholder.style.display = "none";
      };
      reader.readAsDataURL(file);
    }
  }

  resetDropzone(element, placeholder, fileNameElement) {
    element.style.backgroundImage = "none";
    placeholder.style.display = "flex";
    element.classList.remove("hover:brightness-50");
    fileNameElement.style.display = "none";
    fileNameElement.textContent = "";
  }

  dispatchFileType(fileType) {
    const event = new CustomEvent("upscale:filetype", {
      detail: { fileType }
    });
    window.dispatchEvent(event);
  }

  showError(message) {
    // Find the form
    const form = this.element.closest('form');
    if (!form) return;
    
    // Find the main error container
    const errorContainer = form.querySelector('.text-red-500');
    
    if (errorContainer) {
      // Create persistent error message (like Rails form errors)
      const errorP = document.createElement('p');
      errorP.className = 'text-sm text-red-500 mt-1';
      errorP.textContent = message;
      
      // Clear existing errors and add new one
      errorContainer.innerHTML = '';
      errorContainer.appendChild(errorP);
    }
    
    // Also show a toaster notification
    this.showToaster(message);
  }

  showToaster(message) {
    // Create toaster notification
    const toaster = document.createElement('div');
    toaster.className = 'fixed top-4 right-4 z-50 bg-red-900 bg-opacity-90 border border-red-700 text-red-100 px-6 py-4 rounded-lg shadow-lg max-w-md';
    
    // Create inner structure safely without innerHTML
    const container = document.createElement('div');
    container.className = 'flex items-start';
    
    const textContainer = document.createElement('div');
    textContainer.className = 'flex-1';
    
    const title = document.createElement('p');
    title.className = 'text-sm font-medium';
    title.textContent = 'Validation Error';
    
    const messageP = document.createElement('p');
    messageP.className = 'text-sm mt-1';
    messageP.textContent = message; // Safe from XSS
    
    textContainer.appendChild(title);
    textContainer.appendChild(messageP);
    
    const closeButton = document.createElement('button');
    closeButton.className = 'ml-4 text-red-300 hover:text-red-100';
    closeButton.innerHTML = `
      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
      </svg>
    `;
    
    // Bind click handler properly (no inline onclick)
    closeButton.addEventListener('click', () => toaster.remove());
    
    container.appendChild(textContainer);
    container.appendChild(closeButton);
    toaster.appendChild(container);
    
    document.body.appendChild(toaster);
    
    // Auto-remove after 10 seconds (longer timeout)
    setTimeout(() => {
      if (toaster.parentElement) {
        toaster.remove();
      }
    }, 10000);
  }
}